import { useCallback, useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import {
  getQuotePageSettings,
  saveQuotePageSettings,
  createAddon,
  updateAddon,
  deleteAddon,
} from "../api/quotePage";
import { fetchPlaceReviews } from "../components/pay/GoogleReviews";
import { searchBusiness, fetchPlaceDetails } from "../utils/googlePlaces";

// What customers see under their quote: add-ons they can ask for, the 8-hour
// upgrade, Google reviews and the FAQ. None of it changes a quote by itself —
// requests come to you in Follow-ups.

const input =
  "w-full px-3 py-2.5 text-sm border border-birdie-green/25 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-birdie-green/40";

export default function AdminQuotePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await getQuotePageSettings());
    } catch (e) {
      setError(e.message || "Could not load the quote page settings.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function save(patch, msg = "Saved.") {
    setError("");
    try {
      const res = await saveQuotePageSettings(patch);
      setData((d) => ({ ...d, settings: res.settings }));
      setNotice(msg);
    } catch (e) {
      setError(e.message || "Could not save.");
    }
  }

  return (
    <AppShell title="Quote page">
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && !error && (
        <div className="mb-3 rounded-xl border border-birdie-green/25 bg-birdie-green/10 p-3 text-sm text-birdie-green">
          {notice}
        </div>
      )}
      {!data ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-birdie-charcoal/60">
            Shown under the quote on your customers' pay page. When a customer asks for extras, you get an alert and it
            shows up in Follow-ups. Nothing about their quote changes until you send the update.
          </p>

          <Section
            title="Add-ons"
            subtitle="Customers tick these to request them. Each stays off until it has a price."
            action={<Toggle checked={data.settings.show_addons} onChange={(v) => save({ show_addons: v })} label="Show add-ons" />}
          >
            <Addons
              addons={data.addons}
              onChanged={(addons, msg) => {
                setData((d) => ({ ...d, addons }));
                setNotice(msg);
                setError("");
              }}
              onError={setError}
            />
          </Section>

          <Section
            title="Upgrade to 8 hours"
            subtitle="On 4-hour quotes, offers the 8-hour package at the price difference."
            action={<Toggle checked={data.settings.show_upgrade} onChange={(v) => save({ show_upgrade: v })} label="Offer the upgrade" />}
          />

          <Section
            title="Google reviews"
            subtitle="Your best recent Google reviews, pulled live from Google."
            action={
              data.settings.google_place_id ? (
                <Toggle checked={data.settings.show_reviews} onChange={(v) => save({ show_reviews: v })} label="Show reviews" />
              ) : null
            }
          >
            <Reviews
              placeId={data.settings.google_place_id}
              onPick={(placeId) => save({ google_place_id: placeId, show_reviews: true }, "Google listing connected.")}
              onClear={() => save({ google_place_id: null, show_reviews: false }, "Google listing disconnected.")}
            />
          </Section>

          <Section
            title="FAQ"
            subtitle="Answers to the questions customers usually ask."
            action={<Toggle checked={data.settings.show_faq} onChange={(v) => save({ show_faq: v })} label="Show FAQ" />}
          >
            <FaqEditor faq={data.settings.faq} onSave={(faq) => save({ faq }, "FAQ saved.")} />
          </Section>
        </div>
      )}
    </AppShell>
  );
}

// ---- add-ons --------------------------------------------------------------

function Addons({ addons, onChanged, onError }) {
  const [adding, setAdding] = useState(false);

  async function saveOne(id, fields) {
    try {
      const saved = id ? await updateAddon(id, fields) : await createAddon(fields);
      const next = id ? addons.map((a) => (a.id === id ? saved : a)) : [...addons, saved];
      if (!id) setAdding(false);
      onChanged(next, `${saved.name} saved${saved.is_active ? " and showing on quotes" : ""}.`);
      return true;
    } catch (e) {
      onError(e.message || "Could not save the add-on.");
      return false;
    }
  }

  async function remove(a) {
    if (!window.confirm(`Delete ${a.name}?`)) return;
    try {
      await deleteAddon(a.id);
      onChanged(addons.filter((x) => x.id !== a.id), `${a.name} deleted.`);
    } catch (e) {
      onError(e.message || "Could not delete the add-on.");
    }
  }

  return (
    <div className="space-y-3">
      {addons.map((a) => (
        <AddonRow key={a.id} addon={a} onSave={(f) => saveOne(a.id, f)} onDelete={() => remove(a)} />
      ))}
      {adding ? (
        <AddonRow addon={{ name: "", description: "", price_cents: 0, is_active: false }} onSave={(f) => saveOne(null, f)} onDelete={() => setAdding(false)} isNew />
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="text-sm font-semibold text-birdie-green">
          + Add an add-on
        </button>
      )}
    </div>
  );
}

function AddonRow({ addon, onSave, onDelete, isNew }) {
  const [name, setName] = useState(addon.name || "");
  const [description, setDescription] = useState(addon.description || "");
  const [price, setPrice] = useState(addon.price_cents ? String(addon.price_cents / 100) : "");
  const [active, setActive] = useState(Boolean(addon.is_active));
  const [busy, setBusy] = useState(false);
  const priceCents = Math.round(Number(price || 0) * 100);
  const dirty =
    isNew ||
    name !== (addon.name || "") ||
    description !== (addon.description || "") ||
    priceCents !== (addon.price_cents || 0) ||
    active !== Boolean(addon.is_active);

  async function save(nextActive = active) {
    setBusy(true);
    const ok = await onSave({ name: name.trim(), description: description.trim() || null, price_cents: priceCents, is_active: nextActive });
    if (ok) setActive(nextActive);
    setBusy(false);
  }

  return (
    <div className="rounded-xl border border-birdie-green/15 p-3 space-y-2">
      <div className="flex gap-2">
        <input className={`${input} flex-1`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <div className="relative w-28 shrink-0">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-birdie-charcoal/45">$</span>
          <input
            className={`${input} pl-7`}
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Price"
            aria-label="Price"
          />
        </div>
      </div>
      <input
        className={input}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Short description (optional)"
      />
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-birdie-charcoal/75">
          <Toggle
            checked={active}
            onChange={(v) => (isNew ? setActive(v) : save(v))}
            label={`Offer ${name || "this add-on"}`}
            disabled={busy || (!active && priceCents <= 0)}
          />
          {active ? "Offered on quotes" : priceCents > 0 ? "Off" : "Set a price to offer it"}
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={onDelete} className="text-xs font-semibold text-red-600 px-2">
            {isNew ? "Cancel" : "Delete"}
          </button>
          {dirty && (
            <Button size="sm" onClick={() => save()} disabled={busy || !name.trim()}>
              {busy ? "…" : "Save"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- reviews --------------------------------------------------------------

function Reviews({ placeId, onPick, onClear }) {
  const [query, setQuery] = useState("Birdie Bus");
  const [results, setResults] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [manual, setManual] = useState(false);
  const [pasted, setPasted] = useState("");

  useEffect(() => {
    if (!placeId) {
      setCurrent(null);
      return;
    }
    fetchPlaceReviews(placeId)
      .then(setCurrent)
      .catch((e) => setCurrent({ error: e.message }));
  }, [placeId]);

  async function search() {
    setBusy(true);
    setErr("");
    setResults(null);
    try {
      setResults(await searchBusiness(query.trim()));
    } catch (e) {
      setErr(e.message || "Google didn't answer.");
    } finally {
      setBusy(false);
    }
  }

  // A pasted Place ID (or a Google link containing one) is checked with
  // Google before it's saved.
  async function usePasted() {
    const id = (pasted.match(/ChIJ[\w-]+/) || [pasted.trim()])[0];
    setBusy(true);
    setErr("");
    try {
      await fetchPlaceDetails(id);
      onPick(id);
    } catch (e) {
      setErr(`Google didn't recognise that Place ID. ${e.message || ""}`);
    } finally {
      setBusy(false);
    }
  }

  if (placeId) {
    return (
      <div className="text-sm">
        {current?.error ? (
          <p className="text-amber-700 break-words">Connected, but Google didn't return reviews: {current.error}</p>
        ) : current ? (
          <p className="text-birdie-charcoal/80">
            Connected to <strong>{current.name}</strong>
            {current.rating ? ` · ★ ${Number(current.rating).toFixed(1)} (${current.total} reviews)` : ""}
          </p>
        ) : (
          <p className="text-birdie-charcoal/50">Checking Google…</p>
        )}
        <button type="button" onClick={onClear} className="mt-2 text-xs font-semibold text-birdie-green">
          Use a different listing
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input className={`${input} flex-1`} value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Business name" />
        <Button onClick={search} disabled={busy || !query.trim()}>
          {busy ? "…" : "Find"}
        </Button>
      </div>
      {err && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 break-words">
          <strong>Google said:</strong> {err}
          {/REQUEST_DENIED|not.*(enabled|activated|authorized)|ApiNotActivated|ApiTargetBlocked/i.test(err) && (
            <div className="mt-1">
              Your Google Maps key isn't allowed to look up places. In Google Cloud → APIs &amp; Services, enable{" "}
              <strong>Places API (New)</strong>, and if the key has API restrictions, add it there too.
            </div>
          )}
        </div>
      )}
      {results && results.length === 0 && (
        <p className="text-sm text-birdie-charcoal/60">No listing found. Try your exact business name and city, or paste your Place ID below.</p>
      )}
      {results?.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-birdie-green/15 p-3">
          <div className="min-w-0 text-sm">
            <div className="font-semibold text-birdie-charcoal truncate">{r.name}</div>
            <div className="text-xs text-birdie-charcoal/55 truncate">
              {r.address}
              {r.rating ? ` · ★ ${r.rating} (${r.total})` : ""}
            </div>
          </div>
          <Button size="sm" onClick={() => onPick(r.id)}>
            This is us
          </Button>
        </div>
      ))}
      {manual ? (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-birdie-charcoal/60">
            Find your listing on{" "}
            <a
              href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-birdie-green underline"
            >
              Google's Place ID finder
            </a>{" "}
            and paste the ID (starts with ChIJ).
          </p>
          <div className="flex gap-2">
            <input className={`${input} flex-1 font-mono`} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="ChIJ…" aria-label="Place ID" />
            <Button onClick={usePasted} disabled={busy || pasted.trim().length < 10}>
              Use it
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setManual(true)} className="text-xs font-semibold text-birdie-green">
          Or paste your Place ID
        </button>
      )}
    </div>
  );
}

// ---- FAQ ------------------------------------------------------------------

function FaqEditor({ faq, onSave }) {
  const [items, setItems] = useState(faq);
  useEffect(() => setItems(faq), [faq]);
  const dirty = JSON.stringify(items) !== JSON.stringify(faq);
  const set = (i, patch) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-xl border border-birdie-green/15 p-3 space-y-2">
          <input className={`${input} font-semibold`} value={item.q} onChange={(e) => set(i, { q: e.target.value })} placeholder="Question" />
          <textarea className={input} rows={3} value={item.a} onChange={(e) => set(i, { a: e.target.value })} placeholder="Answer" />
          <button type="button" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} className="text-xs font-semibold text-red-600">
            Remove
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => setItems((xs) => [...xs, { q: "", a: "" }])} className="text-sm font-semibold text-birdie-green">
          + Add a question
        </button>
        {dirty && <Button onClick={() => onSave(items.filter((x) => x.q.trim() && x.a.trim()))}>Save FAQ</Button>}
      </div>
    </div>
  );
}

// ---- bits -----------------------------------------------------------------

function Section({ title, subtitle, action, children }) {
  return (
    <section className="bg-white border border-birdie-green/12 rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-accent text-base text-birdie-charcoal">{title}</h2>
          {subtitle && <p className="text-xs text-birdie-charcoal/55 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children && <div className="mt-3">{children}</div>}
    </section>
  );
}

function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 min-w-0 min-h-0 shrink-0 rounded-full transition disabled:opacity-40 ${
        checked ? "bg-birdie-green" : "bg-birdie-charcoal/20"
      }`}
    >
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}
